/**
 * What the launcher lets you change, which is deliberately one thing.
 *
 * A settings screen is the easiest place in a product to lie. Every desktop
 * launcher has an install location, a bandwidth cap and an auto-update toggle,
 * so writing those three is five minutes of work and they look right — and a
 * control that changes nothing is worse than a missing one, because the player
 * sets it, believes it, and then debugs a problem they have already fixed.
 *
 * So this module holds the preferences something in this repository actually
 * reads, and the settings screen offers exactly what is here. Today that is one
 * number. `routes/launcher-settings.tsx` names the three that are absent and
 * says what would have to exist first for each.
 *
 * ## localStorage rather than the Rust side
 *
 * The shell has no preference store: `LauncherState` in `main.rs` holds jobs,
 * a server pool, sessions and a set of chunk hashes, all in memory, and there
 * is no IPC command that persists anything. The thing that honours this
 * preference is the download driver, which is TypeScript, so the preference
 * lives where its reader lives. When the shell grows a config file, this is one
 * function to move rather than a design to unpick.
 */

/** Where the preference lives between runs. Namespaced like `goodbuild.session`. */
const CHUNKS_AT_ONCE_KEY = 'goodbuild.launcher.chunks-at-once';

/**
 * Four, which is `DownloadJob::new`'s own default.
 *
 * Matched on purpose rather than picked. `launcher-core` decided this number
 * for `next_batch`, and a driver that defaulted to something else would make
 * the two halves disagree about what an unconfigured launcher does — which is
 * the sort of difference nobody notices until a support thread is comparing
 * download speeds between machines.
 */
export const CHUNKS_AT_ONCE_DEFAULT = 4;

/**
 * The offered values, as a closed list rather than a free number field.
 *
 * Zero would mean a download that never progresses, which on screen is
 * indistinguishable from a hang — `with_concurrency` clamps it to one for
 * exactly that reason. Something absurd at the top end opens hundreds of
 * simultaneous requests against a CDN from one machine, which is how a player
 * gets themselves rate-limited and reports it as our bug. A list has neither
 * problem and needs no validation message.
 */
export const CHUNKS_AT_ONCE_CHOICES: readonly number[] = [1, 2, 4, 8, 16];

/**
 * How many chunks the driver may have in flight at once.
 *
 * Falls back to the default for anything unreadable — a value from an older
 * build, a hand-edited entry, storage that is unavailable — because a settings
 * value is never worth an exception on a screen that is trying to download a
 * game.
 */
export function chunksAtOnce(): number {
  const stored = read(CHUNKS_AT_ONCE_KEY);
  const value = stored === null ? Number.NaN : Number.parseInt(stored, 10);

  return CHUNKS_AT_ONCE_CHOICES.includes(value) ? value : CHUNKS_AT_ONCE_DEFAULT;
}

export function setChunksAtOnce(value: number): void {
  if (!CHUNKS_AT_ONCE_CHOICES.includes(value)) return;
  write(CHUNKS_AT_ONCE_KEY, String(value));
}

/* -------------------------------------------------------------------------- */
/* The bandwidth cap                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Where the cap lives between runs.
 *
 * **This preference is real now, which it was not when this module was
 * written.** The note at the top of the file is the rule — a control that
 * changes nothing is worse than a missing one — and a cap was absent precisely
 * because `DownloadJob::bandwidth_cap_bps` was a field nothing read. There is a
 * token bucket behind it now (`launcher-core`'s `rate.rs`), and the fetch loop
 * spends against it, so the number a player sets is the number their connection
 * sees.
 */
const BANDWIDTH_CAP_KEY = 'goodbuild.launcher.bandwidth-cap';

/**
 * The offered caps, in bytes per second, with null for unlimited.
 *
 * A closed list again, and for a sharper reason than the concurrency one: a
 * free number field invites a cap of a few hundred bytes per second, which is a
 * download that will not finish this year but reports no error at all. The
 * smallest offered value is 256KB/s, which is slow and still arrives.
 */
export const BANDWIDTH_CAP_CHOICES: readonly (number | null)[] = [
  null,
  256 * 1024,
  1024 * 1024,
  5 * 1024 * 1024,
  10 * 1024 * 1024,
  25 * 1024 * 1024,
];

/**
 * How fast downloads may go, or null for unlimited.
 *
 * Unlimited is the default, because a cap is a thing somebody chooses when
 * their connection is shared and not a thing a launcher should quietly decide
 * on their behalf.
 */
export function bandwidthCap(): number | null {
  const stored = read(BANDWIDTH_CAP_KEY);
  if (stored === null || stored === '') return null;

  const value = Number.parseInt(stored, 10);
  return BANDWIDTH_CAP_CHOICES.includes(value) ? value : null;
}

/* -------------------------------------------------------------------------- */
/* Where games install                                                        */
/* -------------------------------------------------------------------------- */

const LIBRARY_ROOT_KEY = 'goodbuild.launcher.library-root';

/**
 * The folder games install under, or null for the launcher's own.
 *
 * **A path chosen through the native picker and never typed.** The launcher
 * proves the folder is writable before this is stored — by writing to it, since
 * a directory can exist, report itself writable and still refuse — so anything
 * here has already been accepted once. What it cannot promise is that the
 * folder is *still* there: an external drive gets unplugged, a share goes away.
 * The Rust side falls back to its own folder for anything blank or relative,
 * and a download into a folder that has vanished fails at the apply with an
 * ordinary filesystem error rather than silently installing somewhere else.
 *
 * **Changing this moves nothing.** Every install record holds the absolute path
 * of the folder its game is actually in, so games already installed stay where
 * they are and keep working; this decides where the *next* one goes. Moving an
 * existing install would mean copying tens of gigabytes and rewriting a record
 * mid-copy, which is the operation most likely to leave somebody with neither.
 */
export function libraryRoot(): string | null {
  const stored = read(LIBRARY_ROOT_KEY);
  return stored === null || stored.trim() === '' ? null : stored;
}

export function setLibraryRoot(value: string | null): void {
  write(LIBRARY_ROOT_KEY, value === null ? '' : value);
}

/* -------------------------------------------------------------------------- */
/* Keeping games up to date                                                   */
/* -------------------------------------------------------------------------- */

const UPDATE_CHECK_KEY = 'goodbuild.launcher.update-check-minutes';
const AUTO_UPDATE_KEY = 'goodbuild.launcher.auto-update';

/**
 * How often the launcher asks whether anything installed has a newer build.
 *
 * The choices are minutes, and the shortest is fifteen. A build is published by
 * a person pressing a button in CI, not by a stream of events, so checking
 * every minute would be one request per player per minute to learn nothing —
 * and the cost of hearing about an update a quarter of an hour late is a
 * quarter of an hour.
 */
export const UPDATE_CHECK_CHOICES: readonly number[] = [15, 30, 60, 240];

export const UPDATE_CHECK_DEFAULT = 30;

export function updateCheckMinutes(): number {
  const stored = read(UPDATE_CHECK_KEY);
  const value = stored === null ? Number.NaN : Number.parseInt(stored, 10);
  return UPDATE_CHECK_CHOICES.includes(value) ? value : UPDATE_CHECK_DEFAULT;
}

export function setUpdateCheckMinutes(value: number): void {
  if (!UPDATE_CHECK_CHOICES.includes(value)) return;
  write(UPDATE_CHECK_KEY, String(value));
}

/**
 * Whether a newer build downloads on its own.
 *
 * **Off by default, and that is the deliberate half.** Checking is free and
 * telling somebody costs them nothing; *downloading* spends their bandwidth,
 * which on a metered connection is their money, and it can start a 40GB
 * transfer while they are on a video call. Steam defaults this on and it is one
 * of the most complained-about defaults in it.
 *
 * The launcher always checks. What this decides is whether it acts.
 */
export function autoUpdate(): boolean {
  return read(AUTO_UPDATE_KEY) === 'on';
}

export function setAutoUpdate(value: boolean): void {
  write(AUTO_UPDATE_KEY, value ? 'on' : 'off');
}

export function setBandwidthCap(value: number | null): void {
  if (!BANDWIDTH_CAP_CHOICES.includes(value)) return;
  // Unlimited is stored as the empty string rather than removed, so that
  // "chosen unlimited" and "never opened the setting" are the same value read
  // back — they mean the same thing, and a launcher that treated them
  // differently would eventually grow a bug about which one wins.
  write(BANDWIDTH_CAP_KEY, value === null ? '' : String(value));
}

/*
 * Storage can throw, and a launcher that will not open because of it is a worse
 * outcome than one that forgets a setting. Private-mode webviews and a full
 * quota both raise on `getItem` as well as on `setItem`.
 */
function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Nothing to do and nothing worth saying. The default applies.
  }
}
